import axios from 'axios'
import { getToken, removeToken, setToken } from '@/utils/storage'
import router from '@/router'
import { camelToUnderline } from '@/utils/format'
import { API_CONFIG } from '@/config/index'
import { useGlobalStore } from '../store/global'
import { useUserStore } from '../store/user'
import { showLoginConfirm } from '@/utils/dialogHandler'
import { showToast } from 'vant'

const requestCache = new Map()
const pendingRequests = new Map()
const loadingTimers = new Map()
const loadingShown = new Map()
const MIN_LOADING_TIME = 300
const MAX_CACHE_SIZE = 50

// 401登录弹窗防抖：避免多个并发请求同时401时弹出多个弹窗
let loginConfirmShowing = false

const request = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  cache: false,
  cacheExpire: 30000
})

const generateCacheKey = (config) => {
  const { url, method, params, data } = config
  const requestData = method === 'get' ? params : data
  const dataKey = typeof requestData === 'object' ? JSON.stringify(requestData) : requestData;
  return `${url}_${method}_${dataKey}`
}

/**
 * 生成请求唯一key：method + url（不含参数）
 * 同路径的新请求自动取消旧请求，解决参数变化时旧请求未取消的问题
 */
const generatePendingKey = (config) => {
  return `${config.method}_${config.url}`
}

const cleanupRequest = (config) => {
  if (!config) return
  const key = generatePendingKey(config)
  pendingRequests.delete(key)
  const timer = loadingTimers.get(key)
  if (timer) {
    clearTimeout(timer)
    loadingTimers.delete(key)
  }
  loadingShown.delete(key)
}

request.interceptors.request.use(config => {
  const globalStore = useGlobalStore()
  const token = getToken()
  if (token) config.headers.Authorization = token;

  // 支持 baseType 配置项切换 baseURL：
  // - 默认（或不设置）：使用 /api/app（App端接口）
  // - baseType: 'api'：使用 /api（Admin后台接口），用于H5复用后台已有接口
  if (config.baseType === 'api') {
    config.baseURL = API_CONFIG.API_URL
    delete config.baseType
  }

  if (config.hasAuth === true && !token) {
    const userStore = useUserStore()
    const currentPath = router.currentRoute.value.fullPath
    userStore.logout()
    router.replace({ path: '/login', query: { redirect: currentPath, logout: '1' } })
    const controller = new AbortController()
    controller.abort()
    config.signal = controller.signal
    return config
  }

  if (config.method === 'get' && config.params) {
    config.params = camelToUnderline(config.params)
  } else if (['post', 'put', 'patch'].includes(config.method) && config.data) {
    if (!(config.data instanceof FormData)) {
      config.data = camelToUnderline(config.data)
    }
  }

  if (config.cache) {
    const cacheKey = generateCacheKey(config)
    const cachedResponse = requestCache.get(cacheKey)

    if (cachedResponse && Date.now() - cachedResponse.timestamp < (config.cacheExpire || 30000)) {
      return Promise.resolve(cachedResponse.data)
    } else if (cachedResponse) {
      requestCache.delete(cacheKey)
    }
  }

  // AbortController 统一管理：method+url 作为 key，新请求自动取消同路径旧请求
  const pendingKey = generatePendingKey(config)
  if (pendingRequests.has(pendingKey)) {
    const oldController = pendingRequests.get(pendingKey)
    oldController.abort()
    pendingRequests.delete(pendingKey)
    const oldTimer = loadingTimers.get(pendingKey)
    if (oldTimer) {
      clearTimeout(oldTimer)
      loadingTimers.delete(pendingKey)
    }
    if (loadingShown.get(pendingKey)) {
      globalStore.hideLoading()
      loadingShown.delete(pendingKey)
    }
  }

  const controller = new AbortController()
  config.signal = controller.signal
  pendingRequests.set(pendingKey, controller)

  config.requestStartTime = Date.now()

  if (config.loading) {
    loadingShown.set(pendingKey, false)
    const timer = setTimeout(() => {
      globalStore.showLoading()
      loadingShown.set(pendingKey, true)
    }, MIN_LOADING_TIME)
    loadingTimers.set(pendingKey, timer)
  } else {
    loadingTimers.set(pendingKey, null)
  }

  return config
}, error => {
  const globalStore = useGlobalStore()

  if (!axios.isCancel(error) && error.config) {
    const key = generatePendingKey(error.config)
    if (loadingShown.get(key)) {
      globalStore.hideLoading()
    }
    cleanupRequest(error.config)
    globalStore.handleError(error)
  }
  return Promise.reject(error)
})


request.interceptors.response.use(response => {
    const globalStore = useGlobalStore()

    const responseToken = response.headers.authorization;
    if (responseToken) {
      const token = responseToken.replace(/^Bearer\s+/i, '')
      setToken(token)
    }

    const key = generatePendingKey(response.config)
    if (loadingShown.get(key)) {
      globalStore.hideLoading()
    }
    cleanupRequest(response.config)

    const res = response.data

    if (response.config.cache && res.code === 200) {
      const cacheKey = generateCacheKey(response.config)
      if (requestCache.size >= MAX_CACHE_SIZE) {
        const oldestKey = requestCache.keys().next().value
        requestCache.delete(oldestKey)
      }
      requestCache.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      })
    }

    if (res.code === 401) {
      removeToken();
      if (!loginConfirmShowing) {
        loginConfirmShowing = true
        showLoginConfirm('loginExpired').finally(() => {
          loginConfirmShowing = false
        })
      }
      return Promise.reject(res)
    } else if (res.code === 200 && res.status === 'error' && res.msg) {
      showToast(res.msg)
      return Promise.reject(res)
    } else if (res.code !== 200) {
      globalStore.handleError({ response: { data: res } })
      return Promise.reject(res)
    }

    return res
  }, error => {
    const globalStore = useGlobalStore()

    if (error.config) {
      const key = generatePendingKey(error.config)
      if (loadingShown.get(key)) {
        globalStore.hideLoading()
      }
    }
    cleanupRequest(error.config)

    if (!axios.isCancel(error)) {
      globalStore.handleError(error)
    }

    if (error.response && error.response.status === 401) {
      removeToken()
      if (!loginConfirmShowing) {
        loginConfirmShowing = true
        showLoginConfirm('loginExpired').finally(() => {
          loginConfirmShowing = false
        })
      }
    }

    return Promise.reject(error)
  }
)

export const clearRequestCache = (urlPattern = null) => {
  if (urlPattern) {
    for (const [cacheKey] of requestCache) {
      if (cacheKey.includes(urlPattern)) {
        requestCache.delete(cacheKey)
      }
    }
  } else {
    requestCache.clear()
  }
}

export const clearPendingRequests = () => {
  const globalStore = useGlobalStore()
  for (const [key, controller] of pendingRequests) {
    if (key.includes('/notice/popup_announcement')) {
      continue
    }
    controller.abort()
    if (loadingShown.get(key)) {
      globalStore.hideLoading()
    }
  }
  for (const [key] of pendingRequests) {
    if (!key.includes('/notice/popup_announcement')) {
      pendingRequests.delete(key)
      const timer = loadingTimers.get(key)
      if (timer) {
        clearTimeout(timer)
        loadingTimers.delete(key)
      }
      loadingShown.delete(key)
    }
  }
}

export default request

export const debounceRequest = (fn, delay = 300) => {
  let timer = null
  return function(...args) {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      fn.apply(this, args)
    }, delay)
  }
}
